import { useWorkFieldId } from '@/app/work-tabs/work-tab-context';
import type { CustomFieldDefinition, CustomFieldValue } from '@erp/shared';
import { useTranslation } from 'react-i18next';
import { FormField } from '@/components/common/form-field';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { customFieldLabel } from '@/features/custom-fields/lib/custom-field-label';

import { WeeklyScheduleEditor } from './weekly-schedule-editor';

const CLEAR_VALUE = '__none__';

function asText(value: CustomFieldValue | undefined): string {
  if (value === undefined || value === null || typeof value === 'boolean' || Array.isArray(value)) {
    return '';
  }
  return String(value);
}

function asList(value: CustomFieldValue | undefined): string[] {
  return Array.isArray(value) ? value : [];
}

export interface CustomFieldControlProps {
  definition: CustomFieldDefinition;
  unresolvedSourceValue?: string | null | undefined;
  value: CustomFieldValue | undefined;
  onChange: (value: CustomFieldValue) => void;
  error?: string | undefined;
  disabled?: boolean;
  /** Ergebnis eines Formelfeldes; wird schreibgeschützt angezeigt. */
  computed?: string | undefined;
  /** Eindeutige ID, wenn dieselbe Definition mehrfach in Dialogen vorkommt. */
  inputId?: string | undefined;
}

export function CustomFieldControl({
  definition,
  unresolvedSourceValue,
  value,
  onChange,
  error,
  disabled = false,
  computed,
  inputId: suppliedInputId,
}: CustomFieldControlProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const domId = useWorkFieldId();
  const inputId = domId(suppliedInputId ?? `custom-field-${definition.key}`);
  const label = customFieldLabel(definition, i18n.language);
  const locale = i18n.language.toLowerCase().split('-')[0];
  const activeTranslation = definition.translations.find(
    (translation) => translation.locale.toLowerCase() === locale,
  );
  const description =
    activeTranslation?.helpText ??
    activeTranslation?.description ??
    definition.description ??
    undefined;
  const sortedOptions = [...definition.options].sort((a, b) => a.sortOrder - b.sortOrder);
  const invalidClass = error === undefined ? undefined : 'border-destructive';

  if (definition.fieldType === 'WEEKLY_SCHEDULE') {
    return (
      <WeeklyScheduleEditor
        id={inputId}
        label={label}
        value={typeof value === 'string' ? value : null}
        onChange={onChange}
        error={error}
        disabled={disabled}
        required={definition.isRequired}
        description={description}
      />
    );
  }

  if (definition.fieldType === 'FORMULA') {
    return (
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium">{label}</span>
        <output
          id={inputId}
          className="flex h-9 items-center rounded-md border border-border bg-muted/40 px-3 text-sm tabular-nums text-muted-foreground"
        >
          {computed ?? '—'}
        </output>
        {description === undefined ? null : (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>
    );
  }

  if (definition.fieldType === 'CHECKBOX') {
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <Checkbox
            id={inputId}
            checked={value === true}
            disabled={disabled}
            onCheckedChange={(checked) => onChange(checked === true)}
          />
          <Label htmlFor={inputId}>
            {label}
            {definition.isRequired ? <span className="ml-0.5 text-destructive">*</span> : null}
          </Label>
        </div>
        {description === undefined ? null : (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
        {error === undefined ? null : (
          <p role="alert" className="text-xs text-destructive">
            {error}
          </p>
        )}
      </div>
    );
  }

  if (definition.fieldType === 'MULTI_SELECT') {
    const selected = asList(value);
    return (
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-sm font-medium">
          {label}
          {definition.isRequired ? <span className="ml-0.5 text-destructive">*</span> : null}
        </legend>
        <div className="flex flex-col gap-1.5 pt-1">
          {sortedOptions.map((option) => (
            <div key={option.value} className="flex items-center gap-2">
              <Checkbox
                id={`${inputId}-${option.value}`}
                checked={selected.includes(option.value)}
                disabled={disabled}
                onCheckedChange={(checked) =>
                  onChange(
                    checked === true
                      ? [...selected, option.value]
                      : selected.filter((entry) => entry !== option.value),
                  )
                }
              />
              <Label htmlFor={`${inputId}-${option.value}`} className="font-normal">
                {option.label}
              </Label>
            </div>
          ))}
        </div>
        {description === undefined ? null : (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
        {error === undefined ? null : (
          <p role="alert" className="text-xs text-destructive">
            {error}
          </p>
        )}
      </fieldset>
    );
  }

  if (definition.fieldType === 'SELECT') {
    const current = asText(value);
    const unresolved =
      unresolvedSourceValue !== undefined &&
      unresolvedSourceValue !== null &&
      current !== '' &&
      !sortedOptions.some((option) => option.value === current);
    return (
      <FormField
        id={inputId}
        label={label}
        required={definition.isRequired}
        error={error}
        description={
          unresolved
            ? [description, t('migration.nativeFieldUnresolved')].filter(Boolean).join(' ')
            : description
        }
      >
        <Select
          value={current === '' ? CLEAR_VALUE : current}
          disabled={disabled}
          onValueChange={(next) => onChange(next === CLEAR_VALUE ? null : next)}
        >
          <SelectTrigger id={inputId} className={invalidClass}>
            <SelectValue placeholder={t('common.pleaseSelect')}>
              {unresolved ? unresolvedSourceValue : undefined}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={CLEAR_VALUE}>{t('common.none')}</SelectItem>
            {sortedOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>
    );
  }

  if (definition.fieldType === 'TEXTAREA') {
    return (
      <FormField
        id={inputId}
        label={label}
        required={definition.isRequired}
        error={error}
        description={description}
      >
        <Textarea
          id={inputId}
          className={invalidClass}
          value={asText(value)}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
        />
      </FormField>
    );
  }

  const inputType =
    definition.fieldType === 'NUMBER' || definition.fieldType === 'DECIMAL'
      ? 'number'
      : definition.fieldType === 'DATE'
        ? 'date'
        : 'text';

  return (
    <FormField
      id={inputId}
      label={label}
      required={definition.isRequired}
      error={error}
      description={description}
    >
      <Input
        id={inputId}
        type={inputType}
        className={invalidClass}
        step={definition.fieldType === 'DECIMAL' ? 'any' : undefined}
        value={asText(value)}
        disabled={disabled}
        onChange={(event) => {
          const raw = event.target.value;
          if (raw === '') {
            onChange(null);
            return;
          }
          onChange(definition.fieldType === 'NUMBER' ? Number(raw) : raw);
        }}
      />
    </FormField>
  );
}
